import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Wallet } from 'lucide-react';
import { loginUser } from '../api/api';
import { useAuth } from '../context/AuthContext';
import { resolveEffectiveRole, roleHomeRoute } from '../config/roleAccess';

const EyeIcon = ({ show }) =>
  show ? (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
    </svg>
  ) : (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  );

const LoginPage = () => {
  const [form, setForm]       = useState({ email: '', password: '' });
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const { login }   = useAuth();
  const navigate    = useNavigate();

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await loginUser(form);
      const { token, user } = res.data.data;
      login(token, user);
      const orgRole = user?.org_role || user?.organization_role || null;
      const nextRole = resolveEffectiveRole(user?.role, orgRole);
      navigate(roleHomeRoute(nextRole), { replace: true });
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{background:'linear-gradient(135deg,#fdf0f4 0%,#fff 50%,#fef9ec 100%)'}}>
      {/* Background blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full opacity-20 blur-3xl" style={{background:'#cf6a87'}} />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full opacity-20 blur-3xl" style={{background:'#f5cd79'}} />
      </div>

      <div className="relative w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-4">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl shadow-lg mb-2" style={{background:'#c44569'}}>
            <Wallet size={22} color="#fff" strokeWidth={2.5} />
          </div>
          <h1 className="text-2xl font-extrabold text-slate-900">EMS</h1>
          <p className="text-slate-500 text-xs mt-0.5">Expense Management App</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-3xl shadow-xl border p-6" style={{borderColor:'#f9b8ca'}}>
          <h2 className="text-lg font-bold text-slate-800 mb-0.5">Welcome back</h2>
          <p className="text-slate-500 text-xs mb-4">Sign in to your account to continue</p>

          {error && (
            <div className="mb-4 flex items-center gap-2 text-sm px-4 py-3 rounded-xl" style={{background:'#fce0e8',border:'1px solid #f9b8ca',color:'#c44569'}}>
              <svg className="w-4 h-4 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
              </svg>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Email Address</label>
              <input
                className="w-full px-3 py-2 rounded-xl border border-[#f9b8ca] bg-[#fdf0f4] text-slate-900 text-sm outline-none transition-all duration-200 hover:border-[#f3c0ce] focus:border-[#c44569] focus:bg-white focus:ring-4 focus:ring-[#c44569]/15"
                type="email" name="email" value={form.email}
                onChange={handleChange} placeholder="you@example.com" required autoFocus
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs font-semibold text-slate-700">Password</label>
                <Link to="/forgot-password" className="text-xs font-semibold hover:underline transition-colors" style={{color:'#c44569'}}>Forgot Password?</Link>
              </div>
              <div className="relative">
                <input
                  className="w-full px-3 py-2 pr-10 rounded-xl border border-[#f9b8ca] bg-[#fdf0f4] text-slate-900 text-sm outline-none transition-all duration-200 hover:border-[#f3c0ce] focus:border-[#c44569] focus:bg-white focus:ring-4 focus:ring-[#c44569]/15"
                  type={showPass ? 'text' : 'password'} name="password" value={form.password}
                  onChange={handleChange} placeholder="Enter your password" required
                />
                <button type="button" tabIndex={-1}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition-colors duration-150 hover:text-slate-600"
                  onClick={() => setShowPass(s => !s)}>
                  <EyeIcon show={showPass} />
                </button>
              </div>
            </div>
            <button
              className="w-full py-2.5 text-white font-semibold rounded-xl text-sm transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed mt-1 hover:brightness-110"
              style={{background:'#c44569'}}
              type="submit" disabled={loading}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                  Signing in...
                </span>
              ) : 'Sign In'}
            </button>
          </form>

          <p className="text-center text-xs text-slate-500 mt-4">
            Don't have an account?{' '}
            <Link to="/register" className="font-semibold hover:underline" style={{color:'#c44569'}}>Create one</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
